import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ModalDialog } from '../ds';
import { TOAST_SURFACE } from '../ds/toast';
import { deployUpload, userFacingError } from '../../utils/apiV2';
import { UploadedFolder } from '../../utils/folderUpload';
import UploadSource, { UploadSourceValue } from './UploadSource';

/**
 * Deploys code that finished uploading, and says so. The deployment's id, or
 * null when the deploy was refused — the toast has said why, and the upload
 * stays: picking the same folder again reuses it instead of sending it twice.
 */
export const deployUploadedCode = async (
  appId: string,
  { uploadId, manifestSha256, sentCount }: UploadedFolder
): Promise<string | null> => {
  try {
    const { deploymentId } = await deployUpload(appId, uploadId, manifestSha256);
    toast.success(
      // What was sent: the files this account did not have yet. Not a diff
      // of the project — a rename or a delete sends nothing.
      sentCount ? `Deploying new code · ${sentCount} new ${sentCount === 1 ? 'file' : 'files'} sent` : 'Deploying · no new files to send',
      { className: TOAST_SURFACE }
    );
    return deploymentId;
  } catch (e) {
    toast.error(userFacingError(e, 'The new code could not be deployed.'), { className: TOAST_SURFACE });
    return null;
  }
};

/**
 * New code for an upload app, from wherever its code is shown. The same box
 * as Settings › Build & deploy, in a dialog: sending it is a deploy.
 *
 * A click beside it does not close it — an upload can take a while, and a
 * stray click should not lose it. Closing it with an upload under way cancels
 * the upload (UploadSource stops on unmount), so nothing is deployed from it.
 */
const UploadNewCodeDialog = ({ appId, open, onClose, onDeployed }: {
  appId: string,
  open: boolean,
  onClose: () => void,
  onDeployed?: (deploymentId: string) => void,
}) => {
  const [value, setValue] = useState<UploadSourceValue | null>(null);
  const isOpen = useRef(open);

  useEffect(() => {
    isOpen.current = open;
    if (!open) setValue(null);
  }, [open]);

  const onUploaded = async (uploaded: UploadedFolder) => {
    if (!isOpen.current) return;
    const deploymentId = await deployUploadedCode(appId, uploaded);
    if (!deploymentId) return;
    onClose();
    onDeployed?.(deploymentId);
  };

  return (
    <ModalDialog
      open={open}
      onClose={onClose}
      title="Upload new code"
      blurb="Only files that changed are sent, and the new code is deployed."
      width="34rem"
      closeButton
      closeOnClickOutside={false}
    >
      <UploadSource
        appId={appId}
        compact
        value={value}
        onChange={setValue}
        onNext={onUploaded}
        nextLabel="Upload and deploy"
      />
    </ModalDialog>
  );
};

export default UploadNewCodeDialog;
